import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LeaderboardComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

interface LeaderboardSettingsProps {
  component: LeaderboardComponent;
}

const STYLE_OPTIONS = ['default', 'modern', 'minimal'] as const;

export const LeaderboardSettings: React.FC<LeaderboardSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<LeaderboardComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addEntry = () => {
    const newRank = component.settings.entries.length + 1;
    const newEntry = {
      id: `entry-${Date.now()}`,
      rank: newRank,
      name: t('settings.leaderboard.defaultPlayerName', { n: newRank }),
      prize: t('settings.leaderboard.defaultPrize'),
      avatarUrl: '',
    };
    handleUpdate({ entries: [...component.settings.entries, newEntry] });
  };

  const removeEntry = (entryId: string) => {
    if (component.settings.entries.length <= 1) {
      toast.error(t('settings.leaderboard.atLeastOne'));
      return;
    }
    const updatedEntries = component.settings.entries
      .filter((e) => e.id !== entryId)
      .map((e, index) => ({ ...e, rank: index + 1 }));
    handleUpdate({ entries: updatedEntries });
  };

  const updateEntry = (entryId: string, updates: any) => {
    handleUpdate({
      entries: component.settings.entries.map((e) =>
        e.id === entryId ? { ...e, ...updates } : e
      ),
    });
  };

  return (
    <div className="space-y-6">
      {/* Style Selection */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.leaderboard.styleLabel')}</label>
        <select
          value={component.settings.style || 'default'}
          onChange={(e) => handleUpdate({ style: e.target.value as any })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {STYLE_OPTIONS.map((key) => (
            <option key={key} value={key}>
              {t(`settings.onlineUsers.styles.${key}` as const)}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.title')}</label>
        <input
          type="text"
          value={component.settings.title}
          onChange={(e) => handleUpdate({ title: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder={t('settings.leaderboard.titlePlaceholder')}
        />
      </div>

      {/* Show Avatar */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.leaderboard.showAvatar')}</label>
        <ToggleSwitch
          enabled={component.settings.showAvatar ?? false}
          onChange={(enabled) => handleUpdate({ showAvatar: enabled })}
        />
      </div>

      {/* Entries */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">{t('settings.leaderboard.entries')}</label>
          <button
            onClick={addEntry}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus size={16} />
            {t('settings.leaderboard.addEntry')}
          </button>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {component.settings.entries.map((entry) => (
            <div
              key={entry.id}
              className="border border-gray-300 rounded-lg p-3 bg-gray-50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {t('settings.leaderboard.rankNumber', { rank: entry.rank })}
                </span>
                {component.settings.entries.length > 1 && (
                  <button
                    onClick={() => removeEntry(entry.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {/* Player Name */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.leaderboard.playerName')}
                </label>
                <input
                  type="text"
                  value={entry.name}
                  onChange={(e) => updateEntry(entry.id, { name: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                  placeholder={t('settings.leaderboard.playerNamePlaceholder')}
                />
              </div>

              {/* Prize */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {t('settings.leaderboard.prize')}
                </label>
                <input
                  type="text"
                  value={entry.prize}
                  onChange={(e) => updateEntry(entry.id, { prize: e.target.value })}
                  className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                  placeholder={t('settings.leaderboard.prizePlaceholder')}
                />
              </div>

              {/* Avatar URL (optional) */}
              {component.settings.showAvatar && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('settings.leaderboard.avatarUrlOptional')}
                  </label>
                  <input
                    type="text"
                    value={entry.avatarUrl || ''}
                    onChange={(e) => updateEntry(entry.id, { avatarUrl: e.target.value })}
                    className="w-full px-2 py-1 bg-white border border-gray-300 rounded text-sm"
                    placeholder={t('settings.leaderboard.avatarUrlPlaceholder')}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Text Color */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.textColor')}</label>
        <ColorPicker
          color={component.settings.textColor}
          onChange={(color) => handleUpdate({ textColor: color })}
        />
      </div>

      {/* Background Color */}
      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      {/* Padding */}
      <div>
        <label className="block text-sm font-medium mb-2">
          {t('settings.common.padding')}: {component.settings.padding}px
        </label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
      </div>
    </div>
  );
};
